import React from 'react';
import { Upload, Search, GitCompare, File as FileEdit, ShieldCheck, FileCheck } from 'lucide-react';

const STEPS = [
  {
    num: 1,
    title: 'Upload Your Document',
    desc: 'Upload your commercial lease as PDF, Word, or paste the text directly. We handle all standard formats.',
    icon: Upload,
    visual: (
      <div className="flex items-center gap-2 mt-3">
        {['PDF', 'DOCX', 'Scanned'].map((f) => (
          <span key={f} className="px-2.5 py-1 bg-gray-100 rounded-md text-xs font-mono text-gray-600">{f}</span>
        ))}
      </div>
    ),
  },
  {
    num: 2,
    title: 'AI Compliance Analysis',
    desc: 'Our specialist commercial AI analyses every clause against LTA 1954, Lease Code 2007, and 40+ commercial laws.',
    icon: Search,
    visual: (
      <div className="flex items-center gap-2 mt-3">
        <span className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-md">LTA 1954</span>
        <span className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs rounded-md">40+ Laws</span>
      </div>
    ),
  },
  {
    num: 3,
    title: 'Compare Versions',
    desc: 'See a clause-by-clause comparison of your original lease and the compliant rewrite with every change highlighted.',
    icon: GitCompare,
    visual: (
      <div className="flex items-center gap-1.5 mt-3">
        <div className="flex-1 h-8 bg-red-50 border border-red-200 rounded-md flex items-center justify-center">
          <span className="text-[10px] font-semibold text-red-600">ORIGINAL</span>
        </div>
        <div className="flex-1 h-8 bg-emerald-50 border border-emerald-200 rounded-md flex items-center justify-center">
          <span className="text-[10px] font-semibold text-emerald-600">REVISED</span>
        </div>
        <div className="px-2 py-1 bg-amber-50 border border-amber-200 rounded-md">
          <span className="text-[10px] font-semibold text-amber-600">3 CHANGES</span>
        </div>
      </div>
    ),
  },
  {
    num: 4,
    title: 'Finalize Rewrite',
    desc: 'Non-compliant clauses are rewritten to meet commercial lease standards while preserving your commercial terms.',
    icon: FileEdit,
    visual: (
      <div className="mt-3">
        <div className="h-8 bg-emerald-50 border border-emerald-200 rounded-md flex items-center justify-center gap-2">
          <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
          <span className="text-[10px] font-semibold text-emerald-700">REWRITTEN & COMPLIANT</span>
        </div>
      </div>
    ),
  },
  {
    num: 5,
    title: 'Triple Verification',
    desc: 'Three independent AI checks validate accuracy against LTA 1954, commercial best practice, and completeness.',
    icon: ShieldCheck,
    visual: (
      <div className="flex items-center gap-2 mt-3">
        {[1, 2, 3].map((n) => (
          <div key={n} className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
            <span className="text-white text-xs font-bold">{n}</span>
          </div>
        ))}
        <span className="text-xs font-semibold text-gray-500 ml-1">TRIPLE VERIFIED</span>
      </div>
    ),
  },
  {
    num: 6,
    title: 'Final Report',
    desc: 'Receive a comprehensive compliance report with risk score, negotiation recommendations, and downloadable documents.',
    icon: FileCheck,
    visual: (
      <div className="flex items-center gap-3 mt-3">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-md">
          <span className="text-lg font-bold text-emerald-700">98%</span>
          <span className="text-[10px] text-emerald-600 font-medium leading-tight">COMPLIANT<br/>& READY</span>
        </div>
      </div>
    ),
  },
];

export function CommercialProcessSteps() {
  return (
    <section className="py-20 sm:py-28 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16 animate-on-scroll">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">6-Step Commercial Lease Review</h2>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            From upload to final report in minutes, not weeks. Here is exactly what happens to your lease.
          </p>
        </div>

        <div className="relative">
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gray-200 -translate-x-1/2" />

          <div className="space-y-12 md:space-y-0">
            {STEPS.map((step, i) => {
              const isLeft = i % 2 === 0;
              const StepIcon = step.icon;
              return (
                <div
                  key={step.num}
                  className={`animate-on-scroll delay-${(i % 3) * 100 + 100} relative md:flex md:items-start ${isLeft ? 'md:flex-row' : 'md:flex-row-reverse'} md:gap-12`}
                >
                  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 top-2 z-10">
                    <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                      <span className="text-white text-sm font-bold">{step.num}</span>
                    </div>
                  </div>

                  <div className={`md:w-[calc(50%-2rem)] ${isLeft ? 'md:text-right md:pr-4' : 'md:text-left md:pl-4'}`}>
                    <div className={`bg-white border border-gray-200 rounded-2xl p-6 shadow-elevated hover:shadow-elevated-lg transition-shadow duration-300 ${isLeft ? 'md:ml-auto' : 'md:mr-auto'} max-w-md`}>
                      <div className={`flex items-center gap-3 mb-3 ${isLeft ? 'md:flex-row-reverse' : ''}`}>
                        <div className="md:hidden w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs font-bold">{step.num}</span>
                        </div>
                        <div className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center flex-shrink-0">
                          <StepIcon className="w-5 h-5 text-gray-700" />
                        </div>
                        <h3 className="font-semibold text-gray-900">{step.title}</h3>
                      </div>
                      <p className={`text-sm text-gray-500 leading-relaxed ${isLeft ? 'md:text-right' : ''}`}>{step.desc}</p>
                      <div className={isLeft ? 'md:flex md:justify-end' : ''}>{step.visual}</div>
                    </div>
                  </div>

                  <div className="hidden md:block md:w-[calc(50%-2rem)]" />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
